import { numberToWords } from '../utils/numberToWords';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Obra } from './types';
import { getAnexoUrl } from './api';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { GreatVibesBase64 } from '../fonts/GreatVibes-Regular';

// Configure the worker for pdf.js
(pdfjsLib.GlobalWorkerOptions.workerSrc as string) = '/pdf.worker.min.mjs';

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

const formatNumber = (value: number) =>
    new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);

const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

const REPORT_TITLES: Record<string, string> = {
    'resumo-por-categoria': 'Resumo de Custos por Categoria',
    'detalhado-por-categoria': 'Relatório Detalhado por Categoria',
    'detalhado-por-fase': 'Relatório Detalhado por Fase da Obra',
    'quantitativo-por-fase': 'Relatório Quantitativo por Fase (Itens e Preço Médio)',
    'detalhado-por-lancamento': 'Relatório Detalhado por Lançamento',
};

const addFooter = (doc: jsPDF) => {
    const pageCount = doc.getNumberOfPages();
    doc.setFontSize(9);
    doc.setTextColor(100);
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.text(
            `Página ${i} de ${pageCount}`,
            doc.internal.pageSize.width - 14,
            doc.internal.pageSize.height - 10,
            { align: 'right' }
        );
    }
    doc.setTextColor(0);
};

const generateResumoPorCategoria = (doc: jsPDF, data: any[]) => {
    const body: any[] = [];
    const totalGeral = (data || []).reduce((acc, cat) => acc + (Number(cat.total) || 0), 0);

    const buildRows = (categories: any[], level = 0) => {
        categories.forEach((cat) => {
            const isRoot = level === 0;
            const prefix = isRoot ? '' : `${' '.repeat(level * 4)}• `;
            const pct = totalGeral > 0 ? ((Number(cat.total) / totalGeral) * 100).toFixed(1).replace('.', ',') + '%' : '0,0%';

            body.push([
                {
                    content: `${prefix}${cat.nome}`,
                    styles: {
                        fontStyle: isRoot ? 'bold' : 'normal',
                        fillColor: isRoot ? [245, 247, 250] : [255, 255, 255],
                    },
                },
                {
                    content: pct,
                    styles: {
                        halign: 'right',
                        fontStyle: isRoot ? 'bold' : 'normal',
                        fillColor: isRoot ? [245, 247, 250] : [255, 255, 255],
                    },
                },
                {
                    content: formatCurrency(cat.total),
                    styles: {
                        halign: 'right',
                        fontStyle: isRoot ? 'bold' : 'normal',
                        fillColor: isRoot ? [245, 247, 250] : [255, 255, 255],
                    },
                },
            ]);
            if (cat.subcategorias && cat.subcategorias.length > 0) {
                buildRows(cat.subcategorias, level + 1);
            }
        });
    };
    buildRows(data || []);

    autoTable(doc, {
        startY: 30,
        head: [['Categoria / Subcategoria', '% do Total', 'Valor Total']],
        body: body,
        foot: [
            [
                { content: 'Total Geral no Período', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
                { content: formatCurrency(totalGeral), styles: { halign: 'right', fontStyle: 'bold' } },
            ],
        ],
        theme: 'grid',
        headStyles: { fillColor: [220, 220, 220], textColor: 40 },
        footStyles: { fillColor: [220, 220, 220], textColor: 40 },
        columnStyles: {
            1: { halign: 'right', cellWidth: 30 },
            2: { halign: 'right', cellWidth: 45 },
        },
    });
};

const generateDetalhadoPorCategoria = (doc: jsPDF, data: any[]) => {
    let currentY = 30;

    const renderCategory = (categories: any[], level = 0) => {
        categories.forEach((cat) => {
            if (currentY > 255) {
                doc.addPage();
                currentY = 25;
            }

            doc.setFont('helvetica', level === 0 ? 'bold' : 'normal');
            doc.setFontSize(level === 0 ? 13 : 11);
            const indent = 14 + level * 6;
            doc.text(`${cat.nome} — ${formatCurrency(cat.total)}`, indent, currentY);
            doc.setFont('helvetica', 'normal');
            currentY += 5;

            if (cat.items && cat.items.length > 0) {
                autoTable(doc, {
                    startY: currentY,
                    head: [['Descrição', 'Qtd', 'Vl. Unit.', 'Vl. Líquido']],
                    body: cat.items.map((item: any) => [
                        item.descricao,
                        formatNumber(item.quantidade),
                        formatCurrency(item.valor_unitario),
                        formatCurrency(item.valor_liquido),
                    ]),
                    theme: 'grid',
                    styles: { fontSize: 8 },
                    headStyles: { fillColor: [220, 220, 220], textColor: 40 },
                    columnStyles: {
                        1: { halign: 'right', cellWidth: 22 },
                        2: { halign: 'right', cellWidth: 32 },
                        3: { halign: 'right', cellWidth: 35 },
                    },
                    margin: { left: indent },
                });
                currentY = (doc as any).lastAutoTable.finalY + 8;
            } else {
                currentY += 3;
            }

            if (cat.subcategorias && cat.subcategorias.length > 0) {
                renderCategory(cat.subcategorias, level + 1);
            }
        });
    };

    renderCategory(data || []);
};

const generateDetalhadoPorFase = (doc: jsPDF, data: any[]) => {
    let currentY = 30;
    (data || []).forEach((fase) => {
        if (currentY > 250) {
            doc.addPage();
            currentY = 25;
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text(`${fase.nome || 'Sem Fase Definida'} — ${formatCurrency(fase.total)}`, 14, currentY);
        doc.setFont('helvetica', 'normal');
        currentY += 5;

        autoTable(doc, {
            startY: currentY,
            head: [['Data', 'Descrição', 'Fornecedor', 'Qtd', 'Vl. Unit.', 'Vl. Líquido']],
            body: (fase.items || []).map((item: any) => [
                formatDate(item.data_lancamento),
                item.descricao,
                item.fornecedor_nome || '-',
                formatNumber(item.quantidade),
                formatCurrency(item.valor_unitario),
                formatCurrency(item.valor_liquido),
            ]),
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [220, 220, 220], textColor: 40 },
            columnStyles: {
                0: { cellWidth: 22 },
                3: { halign: 'right', cellWidth: 18 },
                4: { halign: 'right', cellWidth: 26 },
                5: { halign: 'right', cellWidth: 28 },
            },
            margin: { left: 14 },
        });
        currentY = (doc as any).lastAutoTable.finalY + 10;
    });
};

const generateQuantitativoPorFase = (doc: jsPDF, data: any[]) => {
    let currentY = 30;
    (data || []).forEach((fase) => {
        if (currentY > 250) {
            doc.addPage();
            currentY = 25;
        }
        const nomeFase = fase.fase_nome || fase.nome || 'Sem Fase Definida';
        const totalFase = Number(fase.subtotal_fase ?? fase.total ?? 0);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text(`${nomeFase} — ${formatCurrency(totalFase)}`, 14, currentY);
        doc.setFont('helvetica', 'normal');
        currentY += 5;

        autoTable(doc, {
            startY: currentY,
            head: [['Descrição do Item', 'Quantidade Total', 'Preço Médio', 'Valor Total']],
            body: (fase.itens || []).map((item: any) => [
                item.descricao,
                formatNumber(item.quantidade_total),
                formatCurrency(item.preco_medio),
                formatCurrency(item.valor_total),
            ]),
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [220, 220, 220], textColor: 40 },
            columnStyles: {
                1: { halign: 'right', cellWidth: 30 },
                2: { halign: 'right', cellWidth: 35 },
                3: { halign: 'right', cellWidth: 35 },
            },
            margin: { left: 14 },
        });
        currentY = (doc as any).lastAutoTable.finalY + 10;
    });
};

const generateDetalhadoPorLancamento = (doc: jsPDF, data: any[]) => {
    const totalGeral = (data || []).reduce((acc, grupo) => acc + (Number(grupo.total_grupo) || 0), 0);
    const body: any[] = [];
    let lancamentoCounter = 0;

    (data || []).forEach((grupo) => {
        if (grupo.items && grupo.items.length > 0) {
            grupo.items.forEach((lancamento: any) => {
                lancamentoCounter++;
                const itemCountInLancamento = lancamento.items.length;

                if (itemCountInLancamento > 0) {
                    lancamento.items.forEach((item: any, itemIndex: number) => {
                        const row: any[] = [];
                        if (itemIndex === 0) {
                            row.push({
                                content: lancamentoCounter,
                                rowSpan: itemCountInLancamento,
                                styles: { valign: 'middle', halign: 'center' },
                            });
                            const notaNumero = lancamento.numero_nota || lancamento.numero_nota_fiscal || 'N/A';
                            const lancamentoInfo =
                                `Data: ${grupo.group_name}\n` +
                                `Fornecedor: ${lancamento.fornecedor_nome}\n` +
                                `Nota: ${notaNumero}`;
                            row.push({
                                content: lancamentoInfo,
                                rowSpan: itemCountInLancamento,
                                styles: { valign: 'middle' },
                            });
                        }

                        row.push(item.descricao);
                        row.push(formatNumber(item.quantidade || 0));
                        row.push(formatCurrency(item.valor_total));

                        if (itemIndex === 0) {
                            row.push({
                                content: formatCurrency(lancamento.valor_total_lancamento),
                                rowSpan: itemCountInLancamento,
                                styles: { valign: 'middle', halign: 'right' },
                            });
                        }

                        body.push(row);
                    });
                }
            });
        }
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const availableWidth = pageWidth - margin * 2;

    autoTable(doc, {
        startY: 30,
        head: [['N°', 'Lançamento', 'Descrição', 'Qtd', 'Vl. Item', 'Valor da Nota']],
        body: body,
        foot: [
            [
                { content: 'Total Geral', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
                { content: formatCurrency(totalGeral), styles: { halign: 'right', fontStyle: 'bold' } },
            ],
        ],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [220, 220, 220], textColor: 40 },
        footStyles: { fillColor: [220, 220, 220], textColor: 40 },
        columnStyles: {
            0: { cellWidth: availableWidth * 0.05, halign: 'center' },
            1: { cellWidth: availableWidth * 0.35 },
            2: { cellWidth: availableWidth * 0.25 },
            3: { cellWidth: availableWidth * 0.07, halign: 'right' },
            4: { cellWidth: availableWidth * 0.14, halign: 'right' },
            5: { cellWidth: availableWidth * 0.14, halign: 'right' },
        },
        margin: { left: margin },
    });
};

export type AnexoMode = 'optimized' | 'high_res' | 'links_only';

export interface GeneratePdfOptions {
    periodo?: { dataInicio?: string; dataFim?: string };
    anexoMode?: AnexoMode;
    signal?: AbortSignal;
    onStatusText?: (status: string) => void;
    skipAutoSave?: boolean;
}

interface AttachmentEntry {
    path: string;
    lancamento: any;
    group_name: string;
    nome_original: string;
}

const extractUniqueAttachments = (data: any[]): AttachmentEntry[] => {
    const attachments: AttachmentEntry[] = [];

    (data || []).forEach((group) => {
        if (group.items) {
            group.items.forEach((lancamento: any) => {
                if (lancamento.items && lancamento.items.length > 0 && lancamento.items[0].anexos) {
                    lancamento.items[0].anexos.forEach((anexo: any) => {
                        if (anexo && anexo.caminho_arquivo) {
                            attachments.push({
                                path: anexo.caminho_arquivo,
                                lancamento: lancamento,
                                group_name: group.group_name,
                                nome_original: anexo.nome_original || 'Comprovante',
                            });
                        }
                    });
                }
            });
        }
    });

    return Array.from(new Map(attachments.map((item) => [item.path, item])).values());
};

export const countReportAttachments = (reportData: any[]): number => {
    return extractUniqueAttachments(reportData).length;
};

const yieldToMainThread = () => new Promise<void>((resolve) => setTimeout(resolve, 12));

const compressAndResizeImageBlob = async (
    blob: Blob,
    maxDimension: number,
    quality: number
): Promise<{ dataUrl: string; width: number; height: number }> => {
    let sourceWidth = 0;
    let sourceHeight = 0;
    let drawSource: CanvasImageSource;
    let cleanup: () => void = () => {};

    if (typeof createImageBitmap === 'function') {
        const bitmap = await createImageBitmap(blob);
        sourceWidth = bitmap.width;
        sourceHeight = bitmap.height;
        drawSource = bitmap;
        cleanup = () => bitmap.close();
    } else {
        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.src = objectUrl;
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Falha ao decodificar imagem'));
        });
        sourceWidth = img.naturalWidth || img.width;
        sourceHeight = img.naturalHeight || img.height;
        drawSource = img;
        cleanup = () => URL.revokeObjectURL(objectUrl);
    }

    try {
        let targetWidth = sourceWidth;
        let targetHeight = sourceHeight;
        const largestSide = Math.max(sourceWidth, sourceHeight);

        if (largestSide > maxDimension && largestSide > 0) {
            const scale = maxDimension / largestSide;
            targetWidth = Math.max(1, Math.round(sourceWidth * scale));
            targetHeight = Math.max(1, Math.round(sourceHeight * scale));
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Contexto 2D indisponível');
        }

        // Fundo branco para evitar fundo preto em PNGs transparentes convertidos para JPEG
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(drawSource, 0, 0, targetWidth, targetHeight);

        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        canvas.width = 0;
        canvas.height = 0;

        return { dataUrl, width: targetWidth, height: targetHeight };
    } finally {
        cleanup();
    }
};

const addAnexosLinksIndex = (
    doc: jsPDF,
    allAttachments: AttachmentEntry[],
    lancamentoNumberMap: Map<number, number>
) => {
    doc.addPage();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Índice de Comprovantes e Anexos (Links Clicáveis)', 14, 20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(
        'Clique em "Abrir Comprovante" para visualizar o documento original diretamente no navegador.',
        14,
        26
    );
    doc.setTextColor(0);

    const rows = allAttachments.map((anexo) => {
        const lancNumber = lancamentoNumberMap.get(anexo.lancamento.lancamento_id) || '-';
        const notaNumero = anexo.lancamento.numero_nota || anexo.lancamento.numero_nota_fiscal || 'N/A';
        const fullUrl = getAnexoUrl('anexos', anexo.path);
        return {
            lancNumber: `Nº ${lancNumber}`,
            data: anexo.group_name || '-',
            fornecedor: `${anexo.lancamento.fornecedor_nome || '-'}\n(Nota: ${notaNumero})`,
            valor: formatCurrency(anexo.lancamento.valor_total_lancamento),
            arquivo: anexo.nome_original || 'Comprovante',
            url: fullUrl,
        };
    });

    autoTable(doc, {
        startY: 31,
        head: [['Lanç.', 'Data', 'Fornecedor / Nota', 'Valor da Nota', 'Arquivo', 'Ação']],
        body: rows.map((r) => [r.lancNumber, r.data, r.fornecedor, r.valor, r.arquivo, 'Abrir Comprovante']),
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2, valign: 'middle' },
        headStyles: { fillColor: [220, 220, 220], textColor: 40, fontStyle: 'bold' },
        columnStyles: {
            0: { cellWidth: 16, halign: 'center', fontStyle: 'bold' },
            1: { cellWidth: 22 },
            2: { cellWidth: 52 },
            3: { cellWidth: 28, halign: 'right' },
            4: { cellWidth: 34 },
            5: { cellWidth: 30, halign: 'center', textColor: [25, 118, 210], fontStyle: 'bold' },
        },
        didDrawCell: (data) => {
            if (data.section === 'body' && data.column.index === 5) {
                const rowItem = rows[data.row.index];
                if (rowItem?.url) {
                    doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: rowItem.url });
                }
            }
        },
        margin: { left: 14, right: 14 },
    });
};

const addAnexos = async (
    doc: jsPDF,
    reportData: any,
    onProgress?: (progress: number) => void,
    options?: GeneratePdfOptions
) => {
    const lancamentoNumberMap = new Map<number, number>();
    let counter = 1;
    (reportData || []).forEach((group: any) => {
        if (group.items) {
            group.items.forEach((lancamento: any) => {
                lancamentoNumberMap.set(lancamento.lancamento_id, counter);
                counter++;
            });
        }
    });

    const allAttachments = extractUniqueAttachments(reportData);
    if (allAttachments.length === 0) return;

    const anexoMode: AnexoMode = options?.anexoMode || 'optimized';
    const signal = options?.signal;
    const onStatusText = options?.onStatusText;

    if (anexoMode === 'links_only') {
        onStatusText?.('Gerando índice de links dos comprovantes...');
        addAnexosLinksIndex(doc, allAttachments, lancamentoNumberMap);
        onProgress?.(90);
        return;
    }

    const totalAttachments = allAttachments.length;
    const isLargeBatch = totalAttachments > 80;
    const maxDimension = anexoMode === 'high_res' ? 2000 : isLargeBatch ? 1000 : 1200;
    const jpegQuality = anexoMode === 'high_res' ? 0.88 : isLargeBatch ? 0.68 : 0.75;
    const pdfRenderScale = anexoMode === 'high_res' ? 1.5 : isLargeBatch ? 1.0 : 1.15;

    // Pool de downloads paralelos (até 10 conexões simultâneas) mantendo a ordem exata
    const CONCURRENCY = 10;
    const prefetchedPromises = new Map<number, Promise<Blob>>();
    let nextFetchIndex = 0;

    const triggerNextPrefetches = () => {
        while (nextFetchIndex < totalAttachments && prefetchedPromises.size < CONCURRENCY) {
            const idx = nextFetchIndex++;
            const path = allAttachments[idx]?.path || '';
            const fullUrl = getAnexoUrl('anexos', path);
            const p = fullUrl
                ? fetch(fullUrl, { signal }).then((res) => {
                      if (!res.ok) {
                          throw new Error(`HTTP ${res.status}`);
                      }
                      return res.blob();
                  })
                : Promise.reject(new Error('Caminho do anexo inválido'));
            // Evita UnhandledPromiseRejection caso falhe antes de ser aguardado no loop principal
            p.catch(() => {});
            prefetchedPromises.set(idx, p);
        }
    };

    triggerNextPrefetches();

    for (let i = 0; i < totalAttachments; i++) {
        if (signal?.aborted) {
            throw new DOMException('Geração de PDF cancelada pelo usuário.', 'AbortError');
        }

        const percentage = 10 + Math.round(((i + 1) / totalAttachments) * 80);
        onProgress?.(percentage);
        onStatusText?.(`Processando comprovante ${i + 1} de ${totalAttachments}...`);

        // Cede um ciclo para a thread principal atualizar a barra de progresso e responder ao botão Cancelar
        await yieldToMainThread();

        const anexo = allAttachments[i];
        const lancamentoNumber = lancamentoNumberMap.get(anexo.lancamento.lancamento_id);

        doc.addPage();
        let startY = 20;

        try {
            const fullUrl = getAnexoUrl('anexos', anexo.path || '');
            const fileExtension = (anexo.path || '').split('.').pop()?.toLowerCase() || '';

            doc.setFontSize(12);
            doc.text(`Anexo vinculado ao lançamento: N° ${lancamentoNumber}`, 14, startY);
            startY += 8;

            const allItemDescriptions = (anexo.lancamento.items || [])
                .map((item: any) => item?.descricao || '')
                .filter(Boolean)
                .join(', ');
            const lancamentoTotal = anexo.lancamento.valor_total_lancamento;

            const itemDetails = [
                `Data: ${anexo.group_name}`,
                `Item: ${allItemDescriptions}`,
                `Vl. Total: ${formatCurrency(lancamentoTotal)}`,
            ].join(' | ');

            doc.setFontSize(10);
            const maxWidth = doc.internal.pageSize.getWidth() - 28;
            const lines = doc.splitTextToSize(itemDetails, maxWidth);
            doc.text(lines, 14, startY);
            const textDimensions = doc.getTextDimensions(lines);
            startY += textDimensions.h + 2;

            const blobPromise = prefetchedPromises.get(i);
            prefetchedPromises.delete(i);
            triggerNextPrefetches();

            if (['jpg', 'jpeg', 'png', 'webp'].includes(fileExtension)) {
                const blob = blobPromise ? await blobPromise : await fetch(fullUrl, { signal }).then((r) => r.blob());

                if (signal?.aborted) {
                    throw new DOMException('Geração de PDF cancelada pelo usuário.', 'AbortError');
                }

                const { dataUrl, width, height } = await compressAndResizeImageBlob(blob, maxDimension, jpegQuality);

                const pdfWidth = doc.internal.pageSize.getWidth() - 28;
                const pdfHeight = doc.internal.pageSize.getHeight() - (startY + 14);
                const imgRatio = width / height;
                const pdfRatio = pdfWidth / pdfHeight;

                let finalWidth: number;
                let finalHeight: number;
                if (imgRatio > pdfRatio) {
                    finalWidth = pdfWidth;
                    finalHeight = pdfWidth / imgRatio;
                } else {
                    finalHeight = pdfHeight;
                    finalWidth = pdfHeight * imgRatio;
                }

                doc.addImage(dataUrl, 'JPEG', 14, startY, finalWidth, finalHeight, `anexo_${i}`, 'FAST');
            } else if (fileExtension === 'pdf') {
                const blob = blobPromise ? await blobPromise : await fetch(fullUrl, { signal }).then((r) => r.blob());

                if (signal?.aborted) {
                    throw new DOMException('Geração de PDF cancelada pelo usuário.', 'AbortError');
                }

                const pdfData = await blob.arrayBuffer();
                const loadingTask = pdfjsLib.getDocument(pdfData);
                const pdf = await loadingTask.promise;

                try {
                    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                        if (signal?.aborted) {
                            throw new DOMException('Geração de PDF cancelada pelo usuário.', 'AbortError');
                        }

                        const page = await pdf.getPage(pageNum);
                        const viewport = page.getViewport({ scale: pdfRenderScale });

                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');
                        canvas.height = viewport.height;
                        canvas.width = viewport.width;

                        await page.render({ canvasContext: context!, viewport: viewport }).promise;

                        const imgData = canvas.toDataURL('image/jpeg', jpegQuality);

                        let contentStartY = startY;
                        if (pageNum > 1) {
                            doc.addPage();
                            doc.setFontSize(12);
                            doc.text(`Anexo vinculado ao lançamento: N° ${lancamentoNumber}`, 14, 20);

                            const detailsForPage = [
                                `Data: ${anexo.group_name}`,
                                `Item: ${allItemDescriptions}`,
                                `Vl. Total: ${formatCurrency(lancamentoTotal)}`,
                            ].join(' | ');

                            doc.setFontSize(10);
                            const linesPage = doc.splitTextToSize(detailsForPage, maxWidth);
                            doc.text(linesPage, 14, 28);
                            const dimsPage = doc.getTextDimensions(linesPage);
                            contentStartY = 28 + dimsPage.h + 2;
                        }

                        const pdfWidth = doc.internal.pageSize.getWidth() - 28;
                        const pdfHeight = doc.internal.pageSize.getHeight() - (contentStartY + 10);
                        const imgRatio = canvas.width / canvas.height;
                        const pdfRatio = pdfWidth / pdfHeight;

                        let finalWidth: number;
                        let finalHeight: number;
                        if (imgRatio > pdfRatio) {
                            finalWidth = pdfWidth;
                            finalHeight = pdfWidth / imgRatio;
                        } else {
                            finalHeight = pdfHeight;
                            finalWidth = pdfHeight * imgRatio;
                        }

                        canvas.width = 0;
                        canvas.height = 0;

                        doc.addImage(imgData, 'JPEG', 14, contentStartY, finalWidth, finalHeight, `anexo_${i}_p${pageNum}`, 'FAST');
                    }
                } finally {
                    await pdf.destroy();
                }
            } else {
                doc.setTextColor(0, 0, 255);
                doc.textWithLink('Clique para ver o arquivo: ' + anexo.nome_original, 14, startY, { url: fullUrl });
                doc.setTextColor(0, 0, 0);
            }
        } catch (error: any) {
            if (error?.name === 'AbortError' || signal?.aborted) {
                throw error;
            }
            console.error(`Erro ao carregar anexo: ${anexo.path}`, error);
            doc.text(`Não foi possível carregar o anexo: ${anexo.nome_original}`, 14, startY);
        }
    }
};

export const generatePdf = async (
    obra: Obra,
    reportData: any,
    reportType: string,
    includeAnexos: boolean,
    onProgress?: (progress: number) => void,
    periodo?: { dataInicio?: string; dataFim?: string },
    extraOptions?: Omit<GeneratePdfOptions, 'periodo'>
) => {
    const signal = extraOptions?.signal;
    const onStatusText = extraOptions?.onStatusText;

    if (onProgress) onProgress(5);
    onStatusText?.('Montando capa e tabelas do relatório...');
    const doc = new jsPDF({ compress: true });
    const reportTitle = REPORT_TITLES[reportType] || 'Relatório da Obra';

    // Capa
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(reportTitle, 105, 20, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(15);
    doc.text(obra.nome, 105, 29, { align: 'center' });

    const periodoTexto =
        periodo?.dataInicio || periodo?.dataFim
            ? `${periodo?.dataInicio ? formatDate(periodo.dataInicio) : 'Início'} até ${
                  periodo?.dataFim ? formatDate(periodo.dataFim) : 'Hoje'
              }`
            : 'Todo o período';

    const custoTotal = (reportData || []).reduce(
        (acc: number, grupo: any) =>
            acc + (Number(grupo.total ?? grupo.total_grupo ?? grupo.subtotal_fase ?? 0)),
        0
    );

    const capaRows: string[][] = [
        ['Período Analisado', periodoTexto],
        ['Data de Emissão', new Date().toLocaleString('pt-BR')],
        ['Endereço', [obra.endereco, obra.municipio, obra.estado].filter(Boolean).join(', ')],
        ['Status da Obra', obra.status],
        ['Tipo / Unidades', `${obra.tipo_de_construcao || '-'} (${obra.quantidade_unidades || 0} unid.)`],
        ['Prazo da Obra', `${formatDate(obra.data_inicio)} a ${formatDate(obra.data_termino_prevista)}`],
    ];
    if (obra.responsaveis && obra.responsaveis.length > 0) {
        capaRows.push([
            'Responsável(is)',
            obra.responsaveis
                .map((r: any) =>
                    r.percentual_participacao != null
                        ? `${r.nome} (${Number(r.percentual_participacao).toFixed(1).replace('.', ',')}%)`
                        : r.nome
                )
                .join(', '),
        ]);
    }
    if (Array.isArray(obra.unidades) && obra.unidades.length > 0) {
        const areaConstrTotal = obra.unidades.reduce((acc: number, u: any) => acc + (Number(u.area_construida) || 0), 0);
        const areaPrivTotal = obra.unidades.reduce(
            (acc: number, u: any) =>
                acc +
                (Number(
                    u.area_privativa_total ??
                        u.area ??
                        ((Number(u.area_construida) || 0) + (Number(u.area_nao_construida) || 0) || 0)
                ) || 0),
            0
        );
        const vagasTotal = obra.unidades.reduce((acc: number, u: any) => acc + (Number(u.vagas) || 0), 0);
        const partesArea: string[] = [];
        if (areaConstrTotal > 0) partesArea.push(`Construída: ${areaConstrTotal.toLocaleString('pt-BR')} m²`);
        if (areaPrivTotal > 0) partesArea.push(`Privativa Total: ${areaPrivTotal.toLocaleString('pt-BR')} m²`);
        if (vagasTotal > 0) partesArea.push(`${vagasTotal} vagas`);
        if (partesArea.length > 0) {
            capaRows.push(['Áreas e Vagas (Total)', partesArea.join(' | ')]);
        }
    }

    autoTable(doc, {
        startY: 38,
        head: [['Informações da Obra e do Relatório', '']],
        body: capaRows,
        theme: 'grid',
        headStyles: { fillColor: [230, 230, 230], fontStyle: 'bold', textColor: 40 },
        columnStyles: {
            0: { cellWidth: 60, fontStyle: 'bold' },
            1: { cellWidth: 'auto' },
        },
    });

    const docRows = [
        obra.cno ? ['CNO', obra.cno] : null,
        obra.inscricao_municipal ? ['Inscrição Municipal', obra.inscricao_municipal] : null,
        obra.matricula_imovel ? ['Matrícula do Imóvel', obra.matricula_imovel] : null,
        obra.numero_alvara ? ['Número do Alvará', obra.numero_alvara] : null,
    ].filter(Boolean) as string[][];

    if (docRows.length > 0) {
        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 5,
            head: [['Documentação Legal', '']],
            body: docRows,
            theme: 'grid',
            headStyles: { fillColor: [230, 230, 230], fontStyle: 'bold', textColor: 40 },
            columnStyles: {
                0: { cellWidth: 60, fontStyle: 'bold' },
                1: { cellWidth: 'auto' },
            },
        });
    }

    const orcamentoInicial = Number(obra.orcamento_inicial) || 0;
    const financeiroRows: any[][] = [['Custo Total no Período', formatCurrency(custoTotal)]];
    if (orcamentoInicial > 0) {
        financeiroRows.push(['Orçamento Inicial da Obra', formatCurrency(orcamentoInicial)]);
        financeiroRows.push(['Saldo do Orçamento', formatCurrency(orcamentoInicial - custoTotal)]);
    }

    autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 5,
        head: [['Resumo Financeiro', '']],
        body: financeiroRows,
        theme: 'grid',
        headStyles: { fillColor: [230, 230, 230], fontStyle: 'bold', textColor: 40 },
        columnStyles: {
            0: { cellWidth: 60, fontStyle: 'bold' },
            1: { cellWidth: 'auto' },
        },
    });

    if (onProgress) onProgress(10);

    // Conteúdo do Relatório
    doc.addPage();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(reportTitle, doc.internal.pageSize.getWidth() / 2, 18, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    switch (reportType) {
        case 'resumo-por-categoria':
            generateResumoPorCategoria(doc, reportData);
            break;
        case 'detalhado-por-categoria':
            generateDetalhadoPorCategoria(doc, reportData);
            break;
        case 'detalhado-por-fase':
            generateDetalhadoPorFase(doc, reportData);
            break;
        case 'quantitativo-por-fase':
            generateQuantitativoPorFase(doc, reportData);
            break;
        case 'detalhado-por-lancamento':
            generateDetalhadoPorLancamento(doc, reportData);
            break;
    }

    // Anexos
    if (includeAnexos && reportType === 'detalhado-por-lancamento') {
        await addAnexos(doc, reportData, onProgress, { ...extraOptions, periodo });
    }

    if (signal?.aborted) {
        throw new DOMException('Geração de PDF cancelada pelo usuário.', 'AbortError');
    }

    onStatusText?.('Finalizando e salvando PDF...');
    if (onProgress) onProgress(95);
    await yieldToMainThread();
    addFooter(doc);

    const safeObraName = (obra.nome || 'obra').toLowerCase().replace(/\s+/g, '_');
    const fileName = `relatorio_${reportType}_${safeObraName}.pdf`;
    const blob = doc.output('blob');

    if (!extraOptions?.skipAutoSave) {
        doc.save(fileName);
    }

    if (onProgress) onProgress(100);
    return { blob, fileName };
};

export const generateReciboPDFBlob = (
    fornecedor: any,
    investidor: any,
    unidade: any,
    obra: any,
    valor: number,
    data: Date,
    descricao: string,
    cidade: string,
    tipoDocumentoBeneficiario: string,
    pagador: any,
    quitaIntegralmente: boolean,
    tipoRecibo: string,
    itens: any[]
): Blob => {
    const doc = new jsPDF('p', 'pt', 'a4');

    const margin = 40;
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = margin + 20;

    doc.setFont('times', 'bold');
    doc.setFontSize(16);
    doc.text('RECIBO DE PAGAMENTO', pageWidth / 2, y, { align: 'center' });
    y += 40;

    doc.setFontSize(14);
    doc.setFont('times', 'normal');
    doc.text(`Valor: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)}`, margin, y);
    y += 30;

    const valorExtenso = numberToWords(valor);

    const getEndereco = () => {
        if (obra) {
            return `${obra.endereco || ''}${obra.municipio ? ', ' + obra.municipio : ''}${obra.estado ? ' - ' + obra.estado : ''}`;
        }
        if (unidade) {
            return `${unidade.endereco || ''}${unidade.bairro ? ', ' + unidade.bairro : ''}${unidade.municipio ? ', ' + unidade.municipio : ''}-${unidade.estado || ''}`;
        }
        return 'Endereço não especificado';
    };

    const enderecoImovel = getEndereco();
    const isMaterialWithItens = tipoRecibo === 'material' && itens && itens.length > 0;

    const textoReferente = isMaterialWithItens
        ? `dos materiais de construção do terreno localizado na ${enderecoImovel}, a seguir:`
        : tipoRecibo === 'material'
        ? `pelo Fornecimento de ${descricao} entregues na ${enderecoImovel}.`
        : `pelo Serviço de ${descricao} localizado na ${enderecoImovel}.`;

    let textoPrincipal =
        `Eu, ${fornecedor.nome_razao_social.toUpperCase()}, inscrito no ${tipoDocumentoBeneficiario} sob o nº ${fornecedor.cnpj_cpf}, ` +
        `declaro para os devidos fins que recebi de ${investidor.nome.toUpperCase()}, `;

    if (pagador) {
        textoPrincipal += `pago por ${pagador.nome.toUpperCase()}, inscrito no ${pagador.documento.length > 14 ? 'CNPJ' : 'CPF'} sob o nº ${pagador.documento}, `;
    }

    textoPrincipal += `a quantia de ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)} (${valorExtenso}). Este valor é referente ao pagamento ${textoReferente}`;

    doc.setFontSize(12);
    const splitText = doc.splitTextToSize(textoPrincipal, pageWidth - margin * 2);
    doc.text(splitText, margin, y, { align: 'justify', maxWidth: pageWidth - margin * 2 });

    y += splitText.length * 15 + 20;

    if (isMaterialWithItens) {
        const body = itens.map((i) => [
            i.descricao,
            i.quantidade,
            new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                i.valor_liquido_proporcional || i.valor_unitario * i.quantidade || i.valor || 0
            ),
        ]);
        body.push(['Total', '', new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)]);

        autoTable(doc, {
            startY: y,
            head: [['Descrição', 'Quantidade', 'Valor']],
            body: body,
            theme: 'grid',
            headStyles: { fillColor: [200, 200, 200], textColor: 0, fontStyle: 'bold', halign: 'center' },
            styles: { font: 'times', fontSize: 12, textColor: 0, halign: 'center' },
            margin: { left: margin, right: margin },
        });
        y = (doc as any).lastAutoTable.finalY + 20;
    }

    if (quitaIntegralmente) {
        const textoQuitacao =
            tipoRecibo === 'material'
                ? 'O presente pagamento quita integralmente o valor acordado pelos materiais mencionados.'
                : 'O presente pagamento quita integralmente o valor acordado pelo serviço mencionado.';

        doc.text(textoQuitacao, margin, y, { align: 'justify', maxWidth: pageWidth - margin * 2 });
        y += 30;
    }

    const dataFull = data.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
    doc.text(`${cidade}, ${dataFull}.`, margin, y);
    y += 60;

    if (y > 700) {
        doc.addPage();
        y = margin + 20;
    }

    doc.addFileToVFS('GreatVibes.ttf', GreatVibesBase64);
    doc.addFont('GreatVibes.ttf', 'GreatVibes', 'normal');

    doc.setFont('GreatVibes', 'normal');
    doc.setFontSize(16);
    const cursiveWidth = doc.getTextWidth(fornecedor.nome_razao_social);

    doc.setFont('times', 'bold');
    doc.setFontSize(12);
    const boldWidth = doc.getTextWidth(fornecedor.nome_razao_social.toUpperCase());

    const lineWidth = Math.max(300, cursiveWidth + 20, boldWidth + 20);

    doc.setFont('GreatVibes', 'normal');
    doc.setFontSize(16);
    doc.text(fornecedor.nome_razao_social, pageWidth / 2, y - 5, { align: 'center' });

    doc.setLineWidth(1);
    doc.line(pageWidth / 2 - lineWidth / 2, y, pageWidth / 2 + lineWidth / 2, y);
    y += 15;

    doc.setFont('times', 'bold');
    doc.setFontSize(12);
    doc.text(fornecedor.nome_razao_social.toUpperCase(), pageWidth / 2, y, { align: 'center' });
    y += 15;

    doc.setFont('times', 'normal');
    doc.text(`${tipoDocumentoBeneficiario}: ${fornecedor.cnpj_cpf}`, pageWidth / 2, y, { align: 'center' });

    return doc.output('blob');
};
